import classNames from 'classnames';
import { type ComponentProps, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Close from '../../icons/Close';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import Heading from '../Heading';
import IconButton from '../IconButton';
import Popover from '../Popover';
import PaidFeatureBadge from '../SubscriptionBadge/PaidFeatureBadge';
import styles from './styles/styles.module.scss';

export type ButtonDefinition = Pick<ComponentProps<typeof Button>, 'disabled' | 'onClick' | 'text' | 'variant'>;

type PopupMenuProps = Pick<ComponentProps<typeof Popover>, 'anchor' | 'placement'> & {
  buttons?: ButtonDefinition[];
  children: React.ReactNode;
  containerClassName?: string;
  'data-denali'?: string;
  headerAndContentClassName?: string;
  hideCloseButton?: boolean;
  isOpen?: boolean;
  onCloseRequest: () => void;
  purchaseTier?: PurchaseTier;
  title?: ReactNode;
  useBottomSheet: boolean;
} & Pick<ComponentProps<typeof Popover>, 'placement' | 'usePortal'>;

export default function PopupMenu({
  anchor,
  buttons,
  children,
  containerClassName,
  'data-denali': dataDenali,
  headerAndContentClassName,
  hideCloseButton = false,
  isOpen = false,
  onCloseRequest,
  placement,
  purchaseTier,
  title,
  useBottomSheet,
  usePortal
}: PopupMenuProps) {
  const intl = useIntl();

  const renderedButtons = useMemo(
    () =>
      buttons?.map(button => (
        <Button key={`popup-menu-${button.variant}-button`} {...(button as ButtonDefinition)} size={useBottomSheet ? 'md' : 'sm'} width="full" />
      )),
    [buttons, useBottomSheet]
  );

  const contents = useMemo(() => {
    const renderHeader = title || !hideCloseButton;
    return (
      <>
        <div className={classNames(styles.headerAndContent, headerAndContentClassName)}>
          {purchaseTier && <PaidFeatureBadge className={styles.badge} featureTier={purchaseTier} size="sm" variant="full" />}
          {renderHeader && (
            <div className={styles.header}>
              {title && (
                <Heading as="h2" className={styles.title} size={useBottomSheet ? '400' : '300'}>
                  {title}
                </Heading>
              )}
              {!hideCloseButton && (
                <IconButton
                  className={styles.closeButton}
                  icon={{ Component: Close }}
                  onClick={onCloseRequest}
                  size="sm"
                  title={intl.formatMessage({ defaultMessage: 'Close' })}
                  variant="secondary"
                />
              )}
            </div>
          )}
          <div className={styles.content}>{children}</div>
        </div>
        {renderedButtons?.length ? <div className={styles.buttons}>{renderedButtons}</div> : null}
      </>
    );
  }, [title, hideCloseButton, headerAndContentClassName, purchaseTier, useBottomSheet, onCloseRequest, intl, children, renderedButtons]);

  return useBottomSheet ? (
    <>
      {anchor}
      <BottomSheet className={classNames(styles.bottomSheet, containerClassName)} isOpen={isOpen} onCloseRequest={onCloseRequest}>
        {contents}
      </BottomSheet>
    </>
  ) : (
    <Popover
      {...setDenaliAttribute(dataDenali || 'PopupMenu')}
      anchor={anchor}
      className={classNames(styles.popover, containerClassName)}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      placement={placement}
      usePortal={usePortal}
    >
      {contents}
    </Popover>
  );
}
